import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DEMO_ACCOUNTS = [
  { email: 'child.demo@mybodytoday.com', password: 'ChildDemo123!', label: 'Child Demo', type: 'child' },
  { email: 'elderly.demo@mybodytoday.com', password: 'ElderlyDemo123!', label: 'Elderly Demo', type: 'elderly' },
  { email: 'condition.demo@mybodytoday.com', password: 'ConditionDemo123!', label: 'Condition Demo', type: 'condition' },
  { email: 'adult.demo@mybodytoday.com', password: 'AdultDemo123!', label: 'Adult Demo', type: 'adult' }
];

const DemoAccountStatus = ({ addLog }) => {
  const [accountStatuses, setAccountStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState({});

  // Test all demo accounts
  const testAllAccounts = async () => {
    setLoading(true);
    addLog('info', 'Testing all demo account credentials...');
    
    const statuses = [];
    
    for (const account of DEMO_ACCOUNTS) {
      try {
        addLog('info', `Testing ${account?.email}...`);
        
        const { data, error } = await supabase?.auth?.signInWithPassword({
          email: account?.email,
          password: account?.password
        });
        
        if (error) {
          statuses?.push({
            ...account,
            success: false,
            error: error?.message,
            userId: null
          });
          addLog('error', `Failed: ${account?.email}`, { error: error?.message });
        } else {
          statuses?.push({
            ...account,
            success: true,
            error: null,
            userId: data?.user?.id,
            email: data?.user?.email,
            emailConfirmed: !!data?.user?.email_confirmed_at,
            lastSignIn: data?.user?.last_sign_in_at
          });
          addLog('success', `Success: ${account?.email}`, { userId: data?.user?.id });
          
          // Sign out immediately
          await supabase?.auth?.signOut();
        }
      } catch (error) {
        statuses?.push({
          ...account,
          success: false,
          error: error?.message || 'Unknown error',
          userId: null
        });
        addLog('error', `Exception: ${account?.email}`, { error: error?.message });
      }
      
      // Small delay between attempts
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setAccountStatuses(statuses);
    setLoading(false);
    
    const successCount = statuses?.filter(s => s?.success)?.length;
    addLog('info', `Test complete: ${successCount}/4 accounts working`);
  };

  // Verify single account credentials
  const verifyCredentials = async (account) => {
    setVerifying(prev => ({ ...prev, [account?.type]: true }));
    addLog('info', `Verifying credentials for ${account?.email}...`);
    
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: account?.email,
        password: account?.password
      });
      
      if (error) {
        addLog('error', `Credential verification failed for ${account?.email}`, { error: error?.message });
      } else {
        addLog('success', `Credentials verified for ${account?.email}`, { userId: data?.user?.id });
        // Sign out immediately after verification
        await supabase?.auth?.signOut();
      }
      
      // Refresh all statuses
      await testAllAccounts();
    } catch (error) {
      addLog('error', `Verification error for ${account?.email}`, { error: error?.message });
    } finally {
      setVerifying(prev => ({ ...prev, [account?.type]: false }));
    }
  };

  const getStatusColor = (status) => {
    return status?.success ? 'text-green-500' : 'text-red-500';
  };

  const getStatusIcon = (status) => {
    return status?.success ? 'CheckCircle2' : 'XCircle';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Users" size={20} />
          Demo Account Status
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={testAllAccounts}
          disabled={loading}
          className="flex items-center gap-2"
        >
          {loading ? (
            <Icon name="Loader2" size={16} className="animate-spin" />
          ) : (
            <Icon name="RefreshCw" size={16} />
          )}
          Test All
        </Button>
      </div>
      
      {accountStatuses?.length === 0 && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Database" size={32} className="mx-auto mb-2 opacity-50" />
          <p className="mb-4">Click "Test All" to verify demo accounts</p>
        </div>
      )}
      
      <div className="space-y-3">
        {accountStatuses?.map((status) => (
          <div
            key={status?.type}
            className="p-4 rounded-lg border border-border bg-background/50 hover:bg-background transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Icon
                    name={getStatusIcon(status)}
                    size={20}
                    className={getStatusColor(status)}
                  />
                  <span className="font-medium text-foreground">{status?.label}</span>
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Email:</span>
                    <code className="text-xs bg-muted px-2 py-0.5 rounded">{status?.email}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Password:</span>
                    <code className="text-xs bg-muted px-2 py-0.5 rounded">{status?.password}</code>
                  </div>
                  {status?.userId && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">User ID:</span>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                        {status?.userId}
                      </code>
                    </div>
                  )}
                  {status?.error && (
                    <div className="flex items-start gap-2 mt-2">
                      <span className="text-muted-foreground">Error:</span>
                      <code className="text-xs bg-red-500/10 text-red-500 px-2 py-0.5 rounded flex-1">
                        {status?.error}
                      </code>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      status?.success ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                    }`}>
                      {status?.success ? '✓ Authentication Works' : '✗ Authentication Failed'}
                    </span>
                    {status?.emailConfirmed !== undefined && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        status?.emailConfirmed ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'
                      }`}>
                        {status?.emailConfirmed ? '✓ Email Confirmed' : '⚠ Not Confirmed'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => verifyCredentials(status)}
                disabled={verifying?.[status?.type] || loading}
                className="ml-4"
              >
                {verifying?.[status?.type] ? (
                  <Icon name="Loader2" size={16} className="animate-spin" />
                ) : (
                  'Retry'
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DemoAccountStatus;